import {Button, ColorSchemeType, DataStatus, Flag, Tooltip} from 'platform/components';
import {Box, Heading, HStack, Show, Text, ThemeColorPath, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {useRef} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {always, defaultTo, isNil, isNotNil} from 'ramda';

import {
  useGetCalculatedItemPriceForCalculationPriceQuery,
  useGetCalculatedItemPriceForCooperationQuery,
  useGetCalculatedItemPriceForDirectPriceQuery,
  useGetCalculatedItemPriceForSubcontractQuery,
  useGetCalculatedItemPriceForTimeNormQuery,
  useGetOrderItemCalculatePriceQuery,
  useLazyGetOrderItemCalculatePriceQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {EMPTY_PLACEHOLDER, Nullish, suffixTestId, TestIdProps, useDebouncedValue} from 'shared/web';

import {EditMaterialItemFormType} from '../EditMaterialItem/types/EditMaterialItemFormType';
import {useIsTruncated} from './hooks/useIsTruncated';

interface ItemPriceProps extends TestIdProps {
  itemId: string;
  serviceCaseId: string;
  serviceOrderId: string;
  serviceJobId: string;
  showPurchasePrice: boolean;
  showMargin: boolean;
  amount: number | Nullish;
  purchasePricePerUnit: number | Nullish;
  purchaseCurrency: string | Nullish;
  sellingPricePerUnit: number | Nullish;
  sellingCurrency: string | Nullish;
  discountRate: number | Nullish;
  vatType: string | Nullish;
  dispensingUnit?: number;
  workType?: string | Nullish;
  priceType?: string;
  assignMechanics?:
    | {
        id?: string | null;
        amount?: number | null;
        ratio?: number | null;
        costCenterId?: string | null;
      }[]
    | Nullish;
  formApi?: UseFormReturn<EditMaterialItemFormType>;
  isEditMaterialItem?: boolean;
  marginUnderMinimumCount?: number | Nullish;
  isDisabled?: boolean;
}

export function ItemPrice(props: ItemPriceProps) {
  const formatCurrency = useFormatCurrency();
  const formatNumber = useFormatNumber();
  const [calculatePrice, calculatePriceQuery] = useLazyGetOrderItemCalculatePriceQuery();

  const purchaseWithoutVatRef = useRef<HTMLHeadingElement>(null);
  const purchaseWithVatRef = useRef<HTMLParagraphElement>(null);
  const articlePriceWithoutVatRef = useRef<HTMLHeadingElement>(null);
  const articlePriceWithVatRef = useRef<HTMLParagraphElement>(null);
  const marginWithoutVatRef = useRef<HTMLHeadingElement>(null);
  const marginWithVatRef = useRef<HTMLParagraphElement>(null);
  const sellingWithoutVatRef = useRef<HTMLHeadingElement>(null);
  const sellingWithVatRef = useRef<HTMLParagraphElement>(null);

  const debouncedPurchasePricePerUnit = useDebouncedValue(
    props.priceType === 'SVCPRICETYPE_TIME_NORM' ? null : props.purchasePricePerUnit,
    300
  );
  const debouncedSellingPricePerUnit = useDebouncedValue(props.sellingPricePerUnit, 300);
  const debouncedVatType = useDebouncedValue(props.vatType, 300);
  const debouncedAmount = useDebouncedValue(props.amount, 300);
  const debouncedDiscount = useDebouncedValue(props.discountRate, 300);
  const debouncedWorkType = useDebouncedValue(props.workType, 300);
  const debouncedMechanics = useDebouncedValue(props.assignMechanics, 300);

  const requestArgs = {
    serviceCaseId: props.serviceCaseId,
    serviceItemId: props.itemId,
    serviceJobId: props.serviceJobId,
    serviceOrderId: props.serviceOrderId,
    body: {
      quantity: debouncedAmount ?? 0,
      sellingPricePerUnit: {
        amount: debouncedSellingPricePerUnit ?? 0,
        currency: props.sellingCurrency ?? '',
      },
      purchasePricePerUnit: {
        amount: debouncedPurchasePricePerUnit ?? 0,
        currency: props.purchaseCurrency ?? '',
      },
      vatType: debouncedVatType ?? '',
      discountRate: debouncedDiscount,
    },
  };

  const timeNormPrices = useGetCalculatedItemPriceForTimeNormQuery(
    {
      ...requestArgs,
      body: {
        ...requestArgs.body,
        workType: debouncedWorkType ?? '',
        assignMechanics:
          debouncedMechanics
            ?.filter((mechanic) => mechanic.id)
            ?.map((mechanic) => ({
              ...mechanic,
              id: mechanic?.id ?? '',
              amount: mechanic?.amount ?? 0,
              ratio: (mechanic?.ratio ?? 0) / 100,
            })) ?? [],
      },
    },
    {skip: props.priceType !== 'SVCPRICETYPE_TIME_NORM'}
  );
  const calculationPrices = useGetCalculatedItemPriceForCalculationPriceQuery(requestArgs, {
    skip: props.priceType !== 'SVCPRICETYPE_CALCULATION_PRICE',
  });
  const directPrices = useGetCalculatedItemPriceForDirectPriceQuery(requestArgs, {
    skip: props.priceType !== 'SVCPRICETYPE_DIRECT_PRICE',
  });
  const cooperationPrices = useGetCalculatedItemPriceForCooperationQuery(requestArgs, {
    skip: props.priceType !== 'SVCPRICETYPE_COOPERATION',
  });
  const subcontractPrices = useGetCalculatedItemPriceForSubcontractQuery(requestArgs, {
    skip: props.priceType !== 'SVCPRICETYPE_SUBCONTRACT',
  });
  const materialPrices = useGetOrderItemCalculatePriceQuery(requestArgs, {
    skip: isNotNil(props.priceType),
  });

  const customCurrencyFormat = (amount: number | Nullish, currency: string | Nullish) => {
    if (isNil(amount) || isNil(currency)) {
      return EMPTY_PLACEHOLDER;
    }

    return formatCurrency(amount, currency, 2);
  };

  const pricesByType = match(props.priceType)
    .with('SVCPRICETYPE_TIME_NORM', () => timeNormPrices)
    .with('SVCPRICETYPE_CALCULATION_PRICE', () => calculationPrices)
    .with('SVCPRICETYPE_DIRECT_PRICE', () => directPrices)
    .with('SVCPRICETYPE_COOPERATION', () => cooperationPrices)
    .with('SVCPRICETYPE_SUBCONTRACT', () => subcontractPrices)
    .otherwise(() => materialPrices);

  const articlePriceStatusIndicatorTooltip = match(materialPrices?.data?.articlePriceIndicator)
    .with('EQUAL', always(i18n.t('entity.warehouse.labels.equalPriceTooltip')))
    .with('HIGHER', always(i18n.t('entity.warehouse.labels.highPriceTooltip')))
    .with('LOWER', always(i18n.t('entity.warehouse.labels.lowPriceTooltip')))
    .with('NO_PRICE', always(i18n.t('entity.warehouse.labels.noPriceTooltip')))
    .otherwise(always(null));

  const articlePriceStatusIndicatorLabel = match(materialPrices?.data?.articlePriceIndicator)
    .with('EQUAL', always(i18n.t('entity.warehouse.labels.equal')))
    .with('HIGHER', always(i18n.t('entity.warehouse.labels.high')))
    .with('LOWER', always(i18n.t('entity.warehouse.labels.low')))
    .with('NO_PRICE', always(i18n.t('entity.warehouse.labels.noPrice')))
    .otherwise(always(''));

  const articlePriceStatusIndicatorColor = match(materialPrices?.data?.articlePriceIndicator)
    .returnType<ColorSchemeType | undefined>()
    .with('EQUAL', always('green'))
    .with('HIGHER', always('orange'))
    .with('LOWER', always('red'))
    .with('NO_PRICE', always('neutral'))
    .otherwise(always(undefined));

  const articleSalePriceBackgroundColor = match(materialPrices?.data?.articlePriceIndicator)
    .returnType<ThemeColorPath | undefined>()
    .with('EQUAL', always('palettes.green.10.100'))
    .with('HIGHER', always('palettes.orange.10.100'))
    .with('LOWER', always('palettes.red.10.100'))
    .with('NO_PRICE', always('palettes.neutral.20.100'))
    .otherwise(always(undefined));

  const marginPercentage = defaultTo(0, pricesByType?.data?.marginTotal?.marginPercentage) * 100;
  const marginPercentageLabel = `${formatNumber(marginPercentage, 2)} %`;

  const purchaseWithoutVat = customCurrencyFormat(
    pricesByType?.data?.purchaseTotalPrice?.withoutVat?.amount,
    pricesByType?.data?.purchaseTotalPrice?.withoutVat?.currency
  );

  const purchaseWithVat = `${customCurrencyFormat(
    pricesByType?.data?.purchaseTotalPrice?.withVat?.amount,
    pricesByType?.data?.purchaseTotalPrice?.withVat?.currency
  )} ${i18n.t('general.labels.withVat')}`;

  const articlePriceWithoutVat = customCurrencyFormat(
    materialPrices?.data?.articleSalePrice?.withoutVat?.amount,
    materialPrices?.data?.articleSalePrice?.withoutVat?.currency
  );

  const articlePriceWithVat = `${customCurrencyFormat(
    materialPrices?.data?.articleSalePrice?.withVat?.amount,
    materialPrices?.data?.articleSalePrice?.withVat?.currency
  )} ${i18n.t('general.labels.withVat')}`;

  const marginPerUnitWithoutVat = customCurrencyFormat(
    pricesByType?.data?.marginTotal?.withoutVat?.amount && props.amount && props.amount !== 0
      ? pricesByType?.data?.marginTotal?.withoutVat?.amount / props.amount
      : null,
    pricesByType?.data?.marginTotal?.withoutVat?.currency
  );

  const marginPerUnitWithVat = customCurrencyFormat(
    pricesByType?.data?.marginTotal?.withVat?.amount && props.amount && props.amount !== 0
      ? pricesByType?.data?.marginTotal?.withVat?.amount / props.amount
      : null,
    pricesByType?.data?.marginTotal?.withVat?.currency
  );

  const marginWithoutVat = `${customCurrencyFormat(
    pricesByType?.data?.marginTotal?.withoutVat?.amount,
    pricesByType?.data?.marginTotal?.withoutVat?.currency
  )}/${marginPerUnitWithoutVat}`;

  const marginWithVat = `${customCurrencyFormat(
    pricesByType?.data?.marginTotal?.withVat?.amount,
    pricesByType?.data?.marginTotal?.withVat?.currency
  )}/${marginPerUnitWithVat} ${i18n.t('general.labels.withVat')}`;

  const sellingWithoutVat = customCurrencyFormat(
    pricesByType?.data?.sellingTotalPrice?.withoutVat?.amount,
    pricesByType?.data?.sellingTotalPrice?.withoutVat?.currency
  );

  const sellingWithVat = `${customCurrencyFormat(
    pricesByType?.data?.sellingTotalPrice?.withVat?.amount,
    pricesByType?.data?.sellingTotalPrice?.withVat?.currency
  )} ${i18n.t('general.labels.withVat')}`;

  const isPurchaseWithoutVatTruncated = useIsTruncated(purchaseWithoutVatRef, purchaseWithoutVat);
  const isPurchaseWithVatTruncated = useIsTruncated(purchaseWithVatRef, purchaseWithVat);
  const isArticlePriceWithoutVatTruncated = useIsTruncated(
    articlePriceWithoutVatRef,
    articlePriceWithoutVat
  );
  const isArticlePriceWithVatTruncated = useIsTruncated(
    articlePriceWithVatRef,
    articlePriceWithVat
  );
  const isSellingWithoutVatTruncated = useIsTruncated(sellingWithoutVatRef, sellingWithoutVat);
  const isSellingWithVatTruncated = useIsTruncated(sellingWithVatRef, sellingWithVat);

  const isMarginUnderMinimum = pricesByType?.data?.marginTotal?.isMarginUnderMinimum ?? false;

  return (
    <DataStatus isLoading={pricesByType?.isLoading} isError={pricesByType?.isError} minHeight={15}>
      <VStack spacing={2}>
        <HStack spacing={2}>
          <Show when={props.isEditMaterialItem}>
            <Box
              border="1px solid"
              borderColor="general.separator"
              borderRadius="small"
              backgroundColor={articleSalePriceBackgroundColor}
              paddingHorizontal={3}
              paddingVertical={2}
            >
              <VStack spacing={1}>
                <Text color="secondary" size="xSmall">
                  {i18n.t('entity.warehouse.labels.articleSalePrice')}
                </Text>
                <HStack spacing={2} align="center">
                  <Tooltip
                    hasAutoWidth
                    placement="top"
                    label={articlePriceWithoutVat}
                    isDisabled={!isArticlePriceWithoutVatTruncated}
                  >
                    <Heading
                      ref={articlePriceWithoutVatRef}
                      isSingleLine
                      size={5}
                      data-testid={suffixTestId('articlePriceWithoutVat', props)}
                    >
                      {articlePriceWithoutVat}
                    </Heading>
                  </Tooltip>
                  <Tooltip placement="top" label={articlePriceStatusIndicatorTooltip}>
                    <Flag
                      label={articlePriceStatusIndicatorLabel}
                      colorScheme={articlePriceStatusIndicatorColor}
                      size="small"
                      data-testid={suffixTestId('articleSalePriceStatus', props)}
                    />
                  </Tooltip>
                </HStack>
                <Tooltip
                  hasAutoWidth
                  placement="top"
                  label={articlePriceWithVat}
                  isDisabled={!isArticlePriceWithVatTruncated}
                >
                  <Text
                    ref={articlePriceWithVatRef}
                    noWrap
                    color="secondary"
                    size="xSmall"
                    data-testid={suffixTestId('articlePriceWithVat', props)}
                  >
                    {articlePriceWithVat}
                  </Text>
                </Tooltip>
              </VStack>
            </Box>
          </Show>
          <Show when={props.showPurchasePrice}>
            <Box flex={1} paddingHorizontal={3} paddingVertical={2}>
              <VStack spacing={1}>
                <Text color="secondary" size="xSmall">
                  {i18n.t('general.labels.totalPurchasePrice')}
                </Text>
                <Tooltip
                  hasAutoWidth
                  placement="top"
                  label={purchaseWithoutVat}
                  isDisabled={!isPurchaseWithoutVatTruncated}
                >
                  <Heading
                    ref={purchaseWithoutVatRef}
                    isSingleLine
                    size={5}
                    color="secondary"
                    data-testid={suffixTestId('purchaseWithoutVat', props)}
                  >
                    {purchaseWithoutVat}
                  </Heading>
                </Tooltip>
                <Tooltip
                  hasAutoWidth
                  placement="top"
                  label={purchaseWithVat}
                  isDisabled={!isPurchaseWithVatTruncated}
                >
                  <Text
                    ref={purchaseWithVatRef}
                    noWrap
                    color="secondary"
                    size="xSmall"
                    data-testid={suffixTestId('purchaseWithVat', props)}
                  >
                    {purchaseWithVat}
                  </Text>
                </Tooltip>
              </VStack>
            </Box>
          </Show>

          <Show when={!props.isEditMaterialItem}>
            <Box flex={1} paddingHorizontal={3} paddingVertical={2}>
              <VStack spacing={1}>
                <Text color="secondary" size="xSmall">
                  {i18n.t('general.labels.sellingPrice')}
                </Text>
                <Tooltip
                  hasAutoWidth
                  placement="top"
                  label={sellingWithoutVat}
                  isDisabled={!isSellingWithoutVatTruncated}
                >
                  <Heading
                    ref={sellingWithoutVatRef}
                    isSingleLine
                    size={5}
                    data-testid={suffixTestId('sellingWithoutVat', props)}
                  >
                    {sellingWithoutVat}
                  </Heading>
                </Tooltip>
                <Tooltip
                  hasAutoWidth
                  placement="top"
                  label={sellingWithVat}
                  isDisabled={!isSellingWithVatTruncated}
                >
                  <Text
                    ref={sellingWithVatRef}
                    noWrap
                    color="secondary"
                    size="xSmall"
                    data-testid={suffixTestId('sellingWithVat', props)}
                  >
                    {sellingWithVat}
                  </Text>
                </Tooltip>
              </VStack>
            </Box>
          </Show>

          <Show when={props.isEditMaterialItem}>
            <Box flex={1} paddingHorizontal={3} paddingVertical={2}>
              <VStack spacing={1}>
                <Text color="secondary" size="xSmall">
                  {i18n.t('general.labels.totalSellingPrice')}
                </Text>
                <Tooltip
                  hasAutoWidth
                  placement="top"
                  label={sellingWithoutVat}
                  isDisabled={!isSellingWithoutVatTruncated}
                >
                  <Heading
                    ref={sellingWithoutVatRef}
                    isSingleLine
                    size={5}
                    data-testid={suffixTestId('sellingWithoutVat', props)}
                  >
                    {sellingWithoutVat}
                  </Heading>
                </Tooltip>
                <Tooltip
                  hasAutoWidth
                  placement="top"
                  label={sellingWithVat}
                  isDisabled={!isSellingWithVatTruncated}
                >
                  <Text
                    ref={sellingWithVatRef}
                    noWrap
                    color="secondary"
                    size="xSmall"
                    data-testid={suffixTestId('sellingWithVat', props)}
                  >
                    {sellingWithVat}
                  </Text>
                </Tooltip>
              </VStack>
            </Box>
          </Show>

          <Show when={props.showMargin}>
            <Box flex={1} paddingLeft={3} paddingVertical={2}>
              <VStack spacing={1}>
                <Text color="secondary" size="xSmall">
                  {i18n.t('entity.warehouse.labels.marginTotalUnitPercent')}
                </Text>
                <HStack spacing={1} align="center">
                  <Box width={31}>
                    <Tooltip hasAutoWidth placement="top" label={marginWithoutVat}>
                      <Heading
                        ref={marginWithoutVatRef}
                        isSingleLine
                        size={5}
                        color={isMarginUnderMinimum ? 'danger' : 'success'}
                        data-testid={suffixTestId('marginWithoutVat', props)}
                      >
                        {marginWithoutVat}
                      </Heading>
                    </Tooltip>
                  </Box>
                  <Flag
                    label={marginPercentageLabel}
                    colorScheme={isMarginUnderMinimum ? 'red' : 'green'}
                    size="small"
                    data-testid={suffixTestId('marginPercentage', props)}
                  />
                </HStack>
                <Box width={43}>
                  <Tooltip hasAutoWidth placement="top" label={marginWithVat}>
                    <Text
                      ref={marginWithVatRef}
                      noWrap
                      color="secondary"
                      size="xSmall"
                      data-testid={suffixTestId('marginWithVat', props)}
                    >
                      {marginWithVat}
                    </Text>
                  </Tooltip>
                </Box>
              </VStack>
            </Box>
          </Show>
        </HStack>

        <HStack>
          <Button
            variant="link"
            leftIcon="navigation/refresh"
            title={i18n.t('entity.warehouse.actions.getCurrentPrice')}
            isLoading={calculatePriceQuery.isLoading}
            isDisabled={props.isDisabled}
            onClick={() => {
              calculatePrice({
                ...requestArgs,
                body: {
                  ...requestArgs.body,
                  getCurrentPrice: true,
                },
              })
                .unwrap()
                .then((data) => {
                  if (!data?.articleSalePrice?.withoutVat?.amount || !props.formApi) {
                    return;
                  }

                  props.formApi.setValue(
                    'sellingPricePerUnit',
                    data?.articleSalePrice?.withoutVat?.amount
                  );
                })
                .catch(handleApiError);
            }}
            data-testid={suffixTestId('actions.getCurrentPrice', props)}
          />
        </HStack>
      </VStack>
    </DataStatus>
  );
}
